<template>
  <div class="st-quantity-stepper">
    <label class="st-quantity-stepper__label" v-if="labelName" :for="props.id">
      {{ labelName }}
    </label>
    <div
      class="st-quantity-stepper__element-group"
      :class="{
        'st-quantity-stepper__element-group__centered-text': hasCenteredText,
      }"
    >
      <StButton
        class="
          st-quantity-stepper__element-group__button
          st-quantity-stepper__element-group__button__minus
        "
        :secondary="!primary"
        :small="true"
        @click="decrement"
        :aria-label="ariaLabelSubtractButton"
        :disabled="disabled || isLowestValidValue"
        :aria-disabled="disabled || isLowestValidValue"
      >
        <StIcon name="minus" />
      </StButton>
      <div
        v-if="hasSelectMenu"
        class="st-quantity-stepper__element-group__select"
      >
        <StSelect
          :id="props.id"
          :model-value="modelValue"
          @update:modelValue="(value) => emit('update:modelValue', value)"
          :items="dropDownItems"
          :has-empty="false"
          :disabled="disabled"
          :invalid="invalid"
          aria-live="polite"
          :aria-label="`${modelValue} ${ariaLabelChangedProperty}`"
        />
      </div>
      <div
        v-else-if="hasInputValue"
        class="st-quantity-stepper__element-group__span"
      >
        <StInput
          :id="props.id"
          :model-value="inputValue"
          :maxLength="maxInputLength"
          :aria-label="`${modelValue} ${ariaLabelChangedProperty}`"
          :invalid="invalid"
          @update:modelValue="submitInputValue"
          @blur="commitInputValue"
        />
      </div>
      <div class="st-quantity-stepper__element-group__span" v-else>
        <span
          aria-live="polite"
          :aria-label="`${modelValue} ${ariaLabelChangedProperty}`"
        >
          {{ modelValue }}
        </span>
      </div>

      <StButton
        class="
          st-quantity-stepper__element-group__button
          st-quantity-stepper__element-group__button__plus
        "
        :secondary="!primary"
        :small="true"
        @click="increment"
        :aria-label="ariaLabelAddButton"
        :disabled="disabled || isHighestValidValue"
        :aria-disabled="disabled || isHighestValidValue"
      >
        <StIcon name="plus" />
      </StButton>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, ref, watch } from "vue";

import StSelect from "@/st-components/select/Select.vue";
import StIcon from "@/st-components/icon/Icon.vue";
import StButton from "@/st-components/button/Button.vue";
import StInput from "@/st-components/input/Input.vue";

const props = defineProps({
  modelValue: {
    type: Number,
  },
  incrementValue: {
    type: Number,
    default: 1,
  },
  maxValue: {
    type: Number,
    default: -1,
  },
  minValue: {
    type: Number,
    default: 0,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  primary: {
    type: Boolean,
    default: false,
  },
  hasSelectMenu: {
    type: Boolean,
    default: true,
  },
  hasInputValue: {
    type: Boolean,
    default: false,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
  },
  ariaLabelAddButton: {
    type: String,
    required: true,
  },
  ariaLabelSubtractButton: {
    type: String,
    required: true,
  },
  ariaLabelChangedProperty: {
    type: String,
    required: true,
  },
  hasCenteredText: {
    type: Boolean,
    default: false,
  },
  labelName: String,
});

const emit = defineEmits(["update:modelValue"]);

const inputValue = ref(props.modelValue?.toString() ?? "");
watch(
  () => props.modelValue,
  (value) => {
    inputValue.value = value?.toString() ?? "";
  },
  { immediate: true },
);

onMounted(() => {
  if (props.minValue !== 0) {
    emit("update:modelValue", props.minValue);
  }
});

const dropDownItems = computed(() => {
  const maxValue =
    props.maxValue === -1 ? 10 * props.incrementValue : props.maxValue;
  const numItems = Math.min(Math.floor(maxValue / props.incrementValue), 3000);
  const startIndex = props.minValue !== 0 ? 1 : 0;
  const items = [];

  for (let i = startIndex; i <= numItems; i++) {
    items.push({
      id: props.incrementValue * i,
      name: props.incrementValue * i,
    });
  }

  return items;
});
const maxInputLength = computed(() =>
  props.maxValue === -1 ? 2 : props.maxValue.toString().length,
);

const isLowestValidValue = computed(() => {
  return props.modelValue === props.minValue;
});

const isHighestValidValue = computed(() => {
  return (
    props.modelValue >= dropDownItems.value[dropDownItems.value.length - 1].id
  );
});

const increment = () => {
  const incrementedValue = props.modelValue + props.incrementValue;
  emit("update:modelValue", incrementedValue);
};

const decrement = () => {
  const incrementedValue = props.modelValue - props.incrementValue;
  emit("update:modelValue", incrementedValue);
};

const getParsedValue = (value) => {
  let parsed = value.replace(/[^\d]/g, "");

  if (parsed === "") {
    return props.minValue;
  }

  parsed = parseInt(parsed, 10);
  const min = props.minValue;
  const max = dropDownItems.value[dropDownItems.value.length - 1].id;
  let corrected = isNaN(parsed) ? props.modelValue : parsed;
  if (corrected <= min) corrected = min;
  if (corrected >= max) corrected = max;
  return corrected;
};

const submitInputValue = (value) => {
  inputValue.value = value;
};
const commitInputValue = () => {
  const parsedValue = getParsedValue(inputValue.value);
  inputValue.value = parsedValue.toString();

  emit("update:modelValue", parsedValue);
};
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";

.st-quantity-stepper {
  &__element-group {
    display: flex;
    align-items: center;
    gap: var(--sm);

    &__centered-text {
      :first-child {
        text-align: center;
      }
    }

    &__select {
      width: 100%;
    }

    &__span {
      width: 100%;
      min-width: rem-calc(32);
      text-align: center;
    }

    &__button {
      width: rem-calc(40);
      height: rem-calc(40);
      border-radius: 50%;
      margin: 0;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
}
</style>
